import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RouteIcon from "@mui/icons-material/Route";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import {
  useTimelineEventDetail,
  useValidationJourneys,
} from "@/hooks/useTroubleshooting";
import { TimelineLaneEnum } from "@/domain/troubleshooting.enum";
import {
  TimelineEvent,
  TimelineEventDetail,
  TimelineEventLinks,
} from "@/domain/troubleshooting.interface";
import { formatInBackofficeLondon } from "@/utils/dateUtils";
import {
  buildEventMapModel,
  extractEventLocation,
  findJourneyForEvent,
  readEventOrigin,
  TimelineWindow,
} from "@/utils/troubleshooting.utils";
import TroubleshootingMap from "./TroubleshootingMap";
import { OutcomeChip, OutcomeIcon } from "./outcome";
import { EventTypeTag } from "./eventType";
import ConditionsTable from "./ConditionsTable";
import { TIMELINE_TIME_FORMAT } from "./TimelineSwimlane";

interface TimelineEventDetailPanelProps {
  participantId: string;
  eventId: string;
  onClose: () => void;
  /** Focus a linked event on the timeline (and open its detail). */
  onFocusEvent: (event: TimelineEvent) => void;
  /** Window used to look up the journey of a GEOFENCE event. */
  window?: TimelineWindow;
  /** "Trace this event": open the Journeys tab on that journey. */
  onTraceJourney?: (journeyId: string) => void;
}

const fmt = (iso?: string) =>
  iso ? formatInBackofficeLondon(iso, TIMELINE_TIME_FORMAT) : "-";

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: "flex", py: 0.5, gap: 2 }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontWeight: 600, minWidth: 150 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      component="div"
      sx={{ wordBreak: "break-word" }}
    >
      {value ?? "-"}
    </Typography>
  </Box>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
    {children}
  </Typography>
);

/**
 * COYN-1507 §17.4: where a DEVICE_ENTER/EXIT, POSITION_SNAPSHOT or backend
 * GEOFENCE event happened — distance, accuracy, inside/outside and a small
 * map with the registered centre and radius. Nothing without coordinates.
 */
const EventLocationSection: React.FC<{
  detail: Pick<TimelineEventDetail, "source" | "type" | "payload">;
}> = ({ detail }) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string, options?: Record<string, unknown>) => string;
  const location = extractEventLocation(detail);
  if (!location) return null;
  const meters = (value?: number) =>
    value === undefined
      ? "-"
      : tt("location.meters", { value: Math.round(value) });
  const model = buildEventMapModel(location, {
    eventPosition: tt("location.map.eventPosition"),
    primaryCentre: tt("location.map.primaryCentre"),
    secondaryCentre: tt("location.map.secondaryCentre"),
  });
  return (
    <Box data-testid="event-detail-location">
      <SectionTitle>{t("location.eventLocation")}</SectionTitle>
      <Field
        label={t("location.lastPosition")}
        value={
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            {`${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`}
          </Box>
        }
      />
      <Field
        label={t("location.eventAccuracy")}
        value={meters(location.accuracy)}
      />
      <Field
        label={t("location.eventDistance")}
        value={meters(location.distance)}
      />
      <Field
        label={t("location.eventRadius")}
        value={meters(location.radius)}
      />
      <Field
        label={t("location.eventInside")}
        value={
          location.inside === undefined
            ? "-"
            : location.inside
              ? t("location.inside")
              : t("location.outside")
        }
      />
      <Box sx={{ mt: 1 }}>
        <TroubleshootingMap
          model={model}
          height={220}
          labels={{
            primary: tt("location.map.primaryCentre"),
            secondary: tt("location.map.secondaryCentre"),
            event: tt("location.map.eventPosition"),
            radius: tt("location.map.radius"),
            openInGoogleMaps: tt("location.map.openInGoogleMaps"),
          }}
        />
      </Box>
    </Box>
  );
};

const TimelineEventDetailPanel: React.FC<TimelineEventDetailPanelProps> = ({
  participantId,
  eventId,
  onClose,
  onFocusEvent,
  window,
  onTraceJourney,
}) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string) => string;
  const {
    data: detail,
    isLoading,
    isError,
    refetch,
  } = useTimelineEventDetail(participantId, eventId);

  const linkEntries = Object.entries(detail?.links || {}).filter(
    ([, value]) => !!value,
  ) as [keyof TimelineEventLinks, string][];
  const linkedEvents = detail?.linkedEvents || [];
  const os = [detail?.osType, detail?.osVersion].filter(Boolean).join(" ");
  const origin = detail ? readEventOrigin(detail) : undefined;

  // §11.4: GEOFENCE-lane events (device or backend) link to their journey.
  const canTrace =
    detail?.lane === TimelineLaneEnum.GEOFENCE && !!window && !!onTraceJourney;
  const { data: journeys } = useValidationJourneys(
    participantId,
    window?.from || "",
    window?.to || "",
    { enabled: canTrace },
  );
  const tracedJourney = canTrace
    ? findJourneyForEvent(journeys, detail?.id)
    : undefined;

  return (
    // Non-modal: the timeline stays usable next to the panel, so focusing a
    // linked event or clicking another marker updates the panel in place.
    <Drawer
      anchor="right"
      variant="persistent"
      open={!!eventId}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 540 },
          p: 3,
          boxShadow: 8,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          // The theme paints every Drawer paper with the sidebar purple; this
          // panel is content, so it uses the regular surface and text colors.
          backgroundColor: "background.paper",
          color: "text.primary",
          borderLeft: 1,
          borderColor: "divider",
        },
        role: "complementary",
        "aria-label": t("detail.title"),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Escape") onClose();
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("detail.title")}
        </Typography>
        <IconButton aria-label={t("detail.close")} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      {isLoading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t("detail.loading")}
          </Typography>
        </Box>
      )}

      {isError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {t("timeline.retry")}
            </Button>
          }
        >
          {t("detail.error")}
        </Alert>
      )}

      {detail && (
        <Box data-testid="event-detail">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h6">{detail.title}</Typography>
            <OutcomeChip outcome={detail.outcome} />
            <EventTypeTag event={detail} />
          </Box>
          {tracedJourney && onTraceJourney && (
            <Button
              size="small"
              startIcon={<RouteIcon />}
              sx={{ mt: 1 }}
              onClick={() => onTraceJourney(tracedJourney.id)}
              data-testid="trace-event"
            >
              {t("detail.traceEvent")}
            </Button>
          )}

          <SectionTitle>{t("detail.whatHappened")}</SectionTitle>
          <Field label={t("detail.type")} value={detail.type} />
          <Field label={t("detail.lane")} value={tt(`lanes.${detail.lane}`)} />
          {detail.schedule ? (
            // COYN-1508: when it was scheduled, when it runs, and whether it
            // ran — its position on the timeline may be a future time.
            <>
              <Field
                label={t("detail.scheduleCreatedAt")}
                value={fmt(detail.schedule.createdAt)}
              />
              <Field
                label={t("detail.scheduledFor")}
                value={fmt(detail.schedule.scheduledFor)}
              />
              <Field
                label={t("detail.executedAt")}
                value={
                  detail.schedule.executedAt
                    ? fmt(detail.schedule.executedAt)
                    : t("detail.notRunYet")
                }
              />
            </>
          ) : (
            <Field
              label={t("detail.occurredAt")}
              value={fmt(detail.occurredAt)}
            />
          )}
          {detail.receivedAt && (
            <Field
              label={t("detail.receivedAt")}
              value={fmt(detail.receivedAt)}
            />
          )}

          <SectionTitle>{t("detail.reason")}</SectionTitle>
          <Typography variant="body1" data-testid="event-detail-reason">
            {detail.reason || t("detail.noReason")}
          </Typography>
          {detail.reasonCode && (
            <Field
              label={t("detail.reasonCode")}
              value={<Chip size="small" label={detail.reasonCode} />}
            />
          )}

          <Box sx={{ mt: 1 }}>
            <Field label={t("detail.source")} value={detail.source} />
            {origin && (
              <Field
                label={t("detail.origin")}
                value={
                  <Box component="span" data-testid="event-detail-origin">
                    {tt(`detail.originValue.${origin}`)}
                  </Box>
                }
              />
            )}
            <Field
              label={t("detail.appVersion")}
              value={detail.appVersion || "-"}
            />
            <Field label={t("detail.os")} value={os || "-"} />
          </Box>

          <EventLocationSection detail={detail} />

          <SectionTitle>{t("detail.conditions")}</SectionTitle>
          {detail.conditions && detail.conditions.length > 0 ? (
            <ConditionsTable
              conditions={detail.conditions}
              reasonCode={detail.reasonCode}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("detail.noConditions")}
            </Typography>
          )}

          <SectionTitle>{t("detail.linkedEntities")}</SectionTitle>
          {linkEntries.length === 0 && linkedEvents.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t("detail.noLinked")}
            </Typography>
          )}
          {linkEntries.map(([key, value]) => (
            <Field
              key={key}
              label={tt(`detail.links.${key}`)}
              value={
                key === "geofenceId" ? (
                  <Link
                    component={RouterLink}
                    to={`/geofences/${value}`}
                    aria-label={t("detail.openGeofence")}
                    sx={{ fontFamily: "monospace" }}
                  >
                    {value}
                  </Link>
                ) : (
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {value}
                  </Box>
                )
              }
            />
          ))}
          {linkedEvents.length > 0 && (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600, mt: 1 }}
              >
                {t("detail.linkedEvents")}
              </Typography>
              <List dense disablePadding>
                {linkedEvents.map((linked) => (
                  <ListItemButton
                    key={linked.id}
                    onClick={() => onFocusEvent(linked)}
                    data-testid={`linked-event-${linked.id}`}
                    // The theme styles ListItem* for the purple sidebar
                    // (light grey text); this list sits on a white panel.
                    sx={{
                      color: "text.primary",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                      <OutcomeIcon outcome={linked.outcome} />
                    </ListItemIcon>
                    <ListItemText
                      primary={linked.title}
                      secondary={`${fmt(linked.occurredAt)} · ${tt(
                        `lanes.${linked.lane}`,
                      )}`}
                      primaryTypographyProps={{
                        sx: { color: "text.primary", fontSize: 14 },
                      }}
                      secondaryTypographyProps={{
                        sx: { color: "text.secondary" },
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}

          <Accordion
            disableGutters
            variant="outlined"
            sx={{ mt: 2 }}
            TransitionProps={{ unmountOnExit: true }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>
                {t("detail.payload")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ mb: 1 }}
                data-testid="event-detail-payload-note"
              >
                {t("detail.payloadUtc")}
              </Typography>
              <Box
                component="pre"
                data-testid="event-detail-payload"
                sx={{
                  m: 0,
                  p: 1,
                  backgroundColor: "rgba(0,0,0,0.04)",
                  borderRadius: 1,
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxHeight: 360,
                  overflow: "auto",
                }}
              >
                {JSON.stringify(detail.payload ?? {}, null, 2)}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Drawer>
  );
};

export default TimelineEventDetailPanel;
